import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Send as SendIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { db, storage } from '../firebase';
import { clientApi } from '../services/clientApi';
import { Conversation, Message } from '../services/messageApi';
import { colors } from '../theme';
import Loading from './Loading';
import VideoCall from './VideoCall';

interface MessagingProps {
  currentUserId: string;
  currentUserName: string;
  isDietitian: boolean;
  dietitian?: any; // Danışan için diyetisyen bilgisi
}

const Messaging: React.FC<MessagingProps> = ({
  currentUserId,
  currentUserName,
  isDietitian,
  dietitian,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const { clearNotificationsByMessageIds } = useNotification();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [showClientList, setShowClientList] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Video call states
  const [isVideoCallOpen, setIsVideoCallOpen] = useState(false);

  useEffect(() => {
    loadConversations();

    if (isDietitian) {
      loadClients();
    }

    const conversationsQuery = query(
      collection(db, 'conversations'),
      where(isDietitian ? 'dietitianId' : 'clientId', '==', currentUserId)
    );

    const unsubscribeConversations = onSnapshot(
      conversationsQuery,
      async (snapshot) => {
        // Her konuşma için okunmamış mesaj sayısını hesapla
        const conversationsWithUnreadCount = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            const conversationId = doc.id;

            // Bu konuşmadaki okunmamış mesajları say
            const unreadMessagesQuery = query(
              collection(db, 'messages'),
              where('conversationId', '==', conversationId),
              where('receiverId', '==', currentUserId),
              where('isRead', '==', false)
            );

            const unreadSnapshot = await getDocs(unreadMessagesQuery);
            const unreadCount = unreadSnapshot.size;

            return {
              id: conversationId,
              dietitianId: data.dietitianId,
              clientId: data.clientId,
              dietitianName: data.dietitianName,
              clientName: data.clientName,
              lastMessageTime:
                data.lastMessageTime?.toDate?.()?.toISOString() ||
                data.lastMessageTime,
              lastMessage: data.lastMessage,
              unreadCount: unreadCount,
              isActiveClient: data.isActiveClient !== undefined ? data.isActiveClient : true, // Varsayılan true
            };
          })
        );

        // Son mesaj zamanına göre sırala (en yeni en üstte)
        conversationsWithUnreadCount.sort((a, b) => {
          const timeA = new Date(a.lastMessageTime).getTime();
          const timeB = new Date(b.lastMessageTime).getTime();
          return timeB - timeA; // Büyükten küçüğe (en yeni en üstte)
        });

        setConversations(conversationsWithUnreadCount);
      }
    );

    return () => {
      unsubscribeConversations();
    };
  }, [currentUserId, isDietitian]);

  // URL'den conversation ID'sini kontrol et ve konuşmayı aç
  useEffect(() => {
    const conversationIdFromUrl = searchParams.get('conversation');
    if (
      conversationIdFromUrl &&
      conversations.length > 0 &&
      !selectedConversation
    ) {
      const conversationToOpen = conversations.find(
        (conv) => conv.id === conversationIdFromUrl
      );
      if (conversationToOpen) {
        setSelectedConversation(conversationToOpen);
        // URL'den parametreyi temizle
        setSearchParams({});
      }
    }
  }, [conversations, searchParams, setSearchParams, selectedConversation]);

  useEffect(() => {
    if (selectedConversation) {
      // conversationId kullanarak mesajları çekiyoruz - daha hızlı ve güvenilir
      const messagesQuery = query(
        collection(db, 'messages'),
        where('conversationId', '==', selectedConversation.id)
      );

      const unsubscribeMessages = onSnapshot(messagesQuery, (snapshot) => {
        const messagesData: Message[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          messagesData.push({
            id: docSnap.id,
            senderId: data.senderId,
            receiverId: data.receiverId,
            content: data.content ?? '',
            timestamp:
              data.timestamp?.toDate?.()?.toISOString() || data.timestamp,
            isRead: data.isRead,
            senderName: data.senderName,
            receiverName: data.receiverName,
            attachmentUrl: data.attachmentUrl,
            attachmentFileName: data.attachmentFileName,
          });
        });
        messagesData.sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        setMessages(messagesData);

        // Bu konuşmadaki tüm mesajların bildirimlerini temizle
        const messageIds = messagesData.map((msg) => msg.id);
        if (messageIds.length > 0) {
          clearNotificationsByMessageIds(messageIds);
        }
      });

      return () => {
        unsubscribeMessages();
      };
    }
  }, [selectedConversation, currentUserId, clearNotificationsByMessageIds]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
    } catch (error) {
      console.error('Konuşmalar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      console.log('Clientlar yükleniyor...');
      const clientsData = await clientApi.getAllClients();
      console.log('Clientlar yüklendi:', clientsData);
      setClients(clientsData);
    } catch (error) {
      console.error('Clientlar yüklenirken hata:', error);
    }
  };

  const startConversationWithClient = async (client: any) => {
    console.log('Client seçildi:', client);
    try {
      let conversationId = `${currentUserId}_${client.id}`;
      if (currentUserId > client.id) {
        conversationId = `${client.id}_${currentUserId}`;
      }

      console.log('Conversation ID:', conversationId);

      const conversationRef = doc(db, 'conversations', conversationId);
      await setDoc(conversationRef, {
        dietitianId: currentUserId,
        clientId: client.id,
        dietitianName: currentUserName,
        clientName: `${client.firstName} ${client.lastName}`,
        lastMessage: '',
        lastMessageTime: new Date(),
        unreadCount: 0,
        isActiveClient: true, // Diyetisyen kendi danışanını seçtiği için aktif
      });

      console.log('Conversation oluşturuldu');

      setSelectedConversation({
        id: conversationId,
        dietitianId: currentUserId,
        clientId: client.id,
        dietitianName: currentUserName,
        clientName: `${client.firstName} ${client.lastName}`,
        lastMessageTime: new Date().toISOString(),
        lastMessage: '',
        unreadCount: 0,
        isActiveClient: true, // Diyetisyen kendi danışanını seçtiği için aktif
      });

      setShowClientList(false);
      console.log('Konuşma seçildi ve client listesi kapatıldı');
    } catch (error) {
      console.error('Konuşma başlatılırken hata:', error);
    }
  };

  const dietitianDisplayName = dietitian
    ? `${dietitian.firstName || ''} ${dietitian.lastName || ''}`.trim() || dietitian.email
    : '';

  const startConversationWithDietitian = async () => {
    if (!dietitian) return;

    console.log('Diyetisyen ile konuşma başlatılıyor:', dietitian);
    try {
      let conversationId = `${dietitian.id}_${currentUserId}`;
      if (dietitian.id > currentUserId) {
        conversationId = `${currentUserId}_${dietitian.id}`;
      }

      console.log('Conversation ID:', conversationId);

      const conversationRef = doc(db, 'conversations', conversationId);
      await setDoc(conversationRef, {
        dietitianId: dietitian.id,
        clientId: currentUserId,
        dietitianName: dietitianDisplayName || dietitian.email,
        clientName: currentUserName,
        lastMessage: '',
        lastMessageTime: new Date(),
        unreadCount: 0,
        isActiveClient: true,
      });

      console.log('Diyetisyen ile conversation oluşturuldu');

      setSelectedConversation({
        id: conversationId,
        dietitianId: dietitian.id,
        clientId: currentUserId,
        dietitianName: dietitianDisplayName || dietitian.email,
        clientName: currentUserName,
        lastMessageTime: new Date().toISOString(),
        lastMessage: '',
        unreadCount: 0,
      });
    } catch (error) {
      console.error('Diyetisyen ile konuşma başlatılırken hata:', error);
    }
  };

  /** Danışan: Bağlı diyetisyenle mevcut sohbeti aç veya yeni sohbet başlat */
  const handleOpenDietitianChat = async () => {
    if (!dietitian) return;
    const existing = conversations.find((c) => c.dietitianId === dietitian.id);
    if (existing) {
      setSelectedConversation(existing);
      await markConversationMessagesAsRead(existing.id, currentUserId);
      const conversationRef = doc(db, 'conversations', existing.id);
      await updateDoc(conversationRef, { unreadCount: 0 });
      setConversations((prev) =>
        prev.map((c) => (c.id === existing.id ? { ...c, unreadCount: 0 } : c))
      );
    } else {
      await startConversationWithDietitian();
    }
  };

  const markConversationMessagesAsRead = async (
    conversationId: string,
    currentUserId: string
  ) => {
    try {
      const messagesQuery = query(
        collection(db, 'messages'),
        where('conversationId', '==', conversationId),
        where('receiverId', '==', currentUserId),
        where('isRead', '==', false)
      );

      const snapshot = await getDocs(messagesQuery);
      const updatePromises = snapshot.docs.map((docSnapshot) =>
        updateDoc(doc(db, 'messages', docSnapshot.id), {
          isRead: true,
        })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error(
        'Konuşma mesajları okundu olarak işaretlenirken hata:',
        error
      );
    }
  };

  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
    'text/plain',
  ];
  const ALLOWED_EXTENSIONS = /\.(pdf|doc|docx|xls|xlsx|jpg|jpeg|png|gif|webp|bmp|txt)$/i;
  const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB

  const sendMessage = async (options?: {
    attachmentUrl?: string;
    attachmentFileName?: string;
    contentOverride?: string;
  }) => {
    const messageContent = (options?.contentOverride !== undefined
      ? options.contentOverride
      : newMessage.trim()) || '';

    if (!messageContent.trim() && !options?.attachmentUrl) return;
    if (!selectedConversation) return;

    if (!options?.attachmentUrl) {
      setNewMessage('');
    }
    setSelectedFile(null);

    try {
      const receiverId =
        selectedConversation.dietitianId === currentUserId
          ? selectedConversation.clientId
          : selectedConversation.dietitianId;

      let receiverName: string;
      if (isDietitian) {
        receiverName = selectedConversation.clientName;
      } else {
        receiverName = dietitian?.email || selectedConversation.dietitianName;
      }

      let conversationId = `${currentUserId}_${receiverId}`;
      if (currentUserId > receiverId) {
        conversationId = `${receiverId}_${currentUserId}`;
      }

      const messagePayload: Record<string, unknown> = {
        conversationId,
        senderId: currentUserId,
        receiverId,
        content: messageContent || (options?.attachmentUrl ? '📎 Dosya' : ''),
        timestamp: new Date(),
        isRead: false,
        senderName: currentUserName,
        receiverName,
      };
      if (options?.attachmentUrl) {
        messagePayload.attachmentUrl = options.attachmentUrl;
        messagePayload.attachmentFileName = options.attachmentFileName || 'Dosya';
      }

      await addDoc(collection(db, 'messages'), messagePayload);

      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);
      const lastMessageDisplay = options?.attachmentFileName
        ? `📎 ${options.attachmentFileName}`
        : messageContent;

      if (conversationDoc.exists()) {
        const currentData = conversationDoc.data();
        const currentUnreadCount = currentData.unreadCount || 0;
        await updateDoc(conversationRef, {
          lastMessage: lastMessageDisplay,
          lastMessageTime: new Date(),
          unreadCount: currentUnreadCount + 1,
        });
      } else {
        await setDoc(conversationRef, {
          dietitianId: selectedConversation.dietitianId,
          clientId: selectedConversation.clientId,
          dietitianName: selectedConversation.dietitianName,
          clientName: selectedConversation.clientName,
          lastMessage: lastMessageDisplay,
          lastMessageTime: new Date(),
          unreadCount: 1,
          isActiveClient: selectedConversation.isActiveClient !== undefined
            ? selectedConversation.isActiveClient
            : true,
        });
      }
    } catch (error) {
      console.error('Mesaj gönderilirken hata:', error);
      if (!options?.attachmentUrl) {
        setNewMessage(messageContent);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isAllowed =
      ALLOWED_FILE_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.test(file.name);
    if (!isAllowed) {
      alert(
        'Desteklenen formatlar: PDF, Word, Excel, resim (JPG, PNG, GIF, WebP), metin (TXT)'
      );
      e.target.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert('Dosya boyutu en fazla 15MB olabilir.');
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
    e.target.value = '';
  };

  const handleSendClick = async () => {
    if (!selectedConversation) return;
    const text = newMessage.trim();
    if (!text && !selectedFile) return;

    if (selectedFile) {
      try {
        setUploadingFile(true);
        const cleanName = selectedFile.name
          .replace(/[^a-zA-Z0-9.-]/g, '_')
          .replace(/\s+/g, '_')
          .toLowerCase();
        const fileName = `messages/${selectedConversation.id}/${Date.now()}_${cleanName}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, selectedFile);
        const downloadURL = await getDownloadURL(storageRef);
        await sendMessage({
          attachmentUrl: downloadURL,
          attachmentFileName: selectedFile.name,
          contentOverride: text,
        });
      } catch (err) {
        console.error('Dosya yüklenirken hata:', err);
        alert('Dosya gönderilemedi. Lütfen tekrar deneyin.');
      } finally {
        setUploadingFile(false);
      }
    } else {
      await sendMessage();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Bugün';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Dün';
    } else {
      return date.toLocaleDateString('tr-TR');
    }
  };

  const startVideoCall = async () => {
    if (!selectedConversation) return;

    try {
      // Firestore'da arama dokümanı oluştur
      await addDoc(collection(db, 'videoCalls'), {
        conversationId: selectedConversation.id,
        from: currentUserId,
        fromName: currentUserName,
        to: isDietitian
          ? selectedConversation.clientId
          : selectedConversation.dietitianId,
        toName: isDietitian
          ? selectedConversation.clientName
          : selectedConversation.dietitianName,
        status: 'calling',
        timestamp: new Date(),
      });

      // Video call modalını aç
      setIsVideoCallOpen(true);
    } catch (error) {
      console.error('Arama başlatma hatası:', error);
      alert('Arama başlatılamadı. Lütfen tekrar deneyin.');
    }
  };

  const handleVideoCallClose = () => {
    setIsVideoCallOpen(false);
  };

  return (
    <>
      {/* Video Call Modal - Sadece arama başlatırken */}
      {isVideoCallOpen && selectedConversation && (
        <VideoCall
          open={isVideoCallOpen}
          onClose={handleVideoCallClose}
          conversationId={selectedConversation.id}
          currentUserId={currentUserId}
          currentUserName={currentUserName}
          otherUserId={
            isDietitian
              ? selectedConversation.clientId
              : selectedConversation.dietitianId
          }
          otherUserName={
            isDietitian
              ? selectedConversation.clientName
              : selectedConversation.dietitianName
          }
          isInitiator={true}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          height: '97vh',
          borderRadius: 0,
          overflow: 'hidden',
          boxShadow: 'none',
          background: '#FFFFFF',
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: isMobile ? '100%' : 350,
            minWidth: isMobile ? '100%' : 350,
            maxWidth: isMobile ? '100%' : 350,
            display: selectedConversation && isMobile ? 'none' : 'flex',
            flexDirection: 'column',
            background: '#F9FAF7',
            borderRight: isMobile ? 'none' : '1px solid #e2e8f0',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              p: 2.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '2px solid #e2e8f0',
              background: 'linear-gradient(135deg, #F9FAF7 0%, #FFFFFF 100%)',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#263238',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              💬 Mesajlar
            </Typography>
            {isDietitian && (
              <IconButton
                onClick={() => setShowClientList(!showClientList)}
                sx={{
                  bgcolor: '#43A047',
                  color: 'white',
                  width: 36,
                  height: 36,
                  '&:hover': {
                    bgcolor: '#388E3C',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Client List */}
          {showClientList && isDietitian && (
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Client Seç ({clients.length} client bulundu)
              </Typography>
              {clients.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Client bulunamadı
                </Typography>
              ) : (
                <List dense>
                  {clients.map((client) => (
                    <ListItem key={client.id} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          console.log('Client item tıklandı:', client);
                          startConversationWithClient(client);
                        }}
                        sx={{ borderRadius: 1, mb: 0.5 }}
                      >
                        <ListItemAvatar>
                          <Avatar>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${client.firstName} ${client.lastName}`}
                          secondary={client.email}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Diyetisyenimle sohbet et butonu (Danışanlar için - sadece henüz konuşma yoksa) */}
          {!isDietitian && dietitian && !conversations.some((c) => c.dietitianId === dietitian.id) && (
            <Box sx={{ p: 2, flexShrink: 0 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenDietitianChat}
                sx={{
                  bgcolor: '#43A047',
                  color: 'white',
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(67, 160, 71, 0.3)',
                  '&:hover': {
                    bgcolor: '#388E3C',
                    boxShadow: '0 6px 16px rgba(67, 160, 71, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Diyetisyenimle sohbet et
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center', mt: 1 }}
              >
                {dietitianDisplayName || dietitian.email}
              </Typography>
            </Box>
          )}

          {/* Conversations List */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {loading && conversations.length === 0 ? (
              <Loading
                size="large"
                variant="wave"
                mode="inline"
                message="Mesajlar yükleniyor..."
              />
            ) : (
              <List>
                {conversations.map((conversation) => {
                  const hasUnread = conversation.unreadCount > 0;
                  return (
                    <ListItem key={conversation.id} disablePadding>
                      <ListItemButton
                        selected={selectedConversation?.id === conversation.id}
                        onClick={async () => {
                          setSelectedConversation(conversation);
                          // Konuşmaya tıklanınca mesajları okundu olarak işaretle
                          await markConversationMessagesAsRead(
                            conversation.id,
                            currentUserId
                          );
                          // Conversation'un unreadCount'unu sıfırla
                          const conversationRef = doc(
                            db,
                            'conversations',
                            conversation.id
                          );
                          await updateDoc(conversationRef, {
                            unreadCount: 0,
                          });
                          // State'i anında güncelle - görsel olarak hemen değişsin
                          setConversations((prevConversations) =>
                            prevConversations.map((conv) =>
                              conv.id === conversation.id
                                ? { ...conv, unreadCount: 0 }
                                : conv
                            )
                          );
                        }}
                        sx={{
                          py: 2,
                          px: 2,
                          borderBottom: '1px solid #e2e8f0',
                          transition: 'all 0.2s ease',
                          backgroundColor: hasUnread ? '#E8F5E9' : '#FFFFFF',
                          ...(hasUnread && {
                            borderLeft: '4px solid #43A047',
                          }),
                          '&:hover': {
                            background: hasUnread
                              ? 'rgba(67, 160, 71, 0.15)'
                              : 'rgba(165, 214, 167, 0.08)',
                            transform: 'translateX(4px)',
                          },
                          '&.Mui-selected': {
                            background: hasUnread
                              ? 'rgba(67, 160, 71, 0.2)'
                              : 'rgba(165, 214, 167, 0.15)',
                            borderLeft: '4px solid #43A047',
                            '&:hover': {
                              background: hasUnread
                                ? 'rgba(67, 160, 71, 0.25)'
                                : 'rgba(165, 214, 167, 0.2)',
                            },
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: '#43A047',
                              width: 48,
                              height: 48,
                              fontWeight: 700,
                            }}
                          >
                            {isDietitian
                              ? conversation.clientName.charAt(0).toUpperCase()
                              : conversation.dietitianName
                                  .charAt(0)
                                  .toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          sx={{
                            minWidth: 0,
                            overflow: 'hidden',
                          }}
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 0.5,
                                minWidth: 0,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    color: '#263238',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  {isDietitian
                                    ? conversation.clientName
                                    : conversation.dietitianName}
                                </Typography>
                                {isDietitian && conversation.isActiveClient === false && (
                                  <Chip
                                    icon={<PersonAddIcon sx={{ fontSize: 14 }} />}
                                    label="Yeni"
                                    size="small"
                                    sx={{
                                      bgcolor: '#FF9800',
                                      color: 'white',
                                      fontWeight: 600,
                                      height: 20,
                                      fontSize: '0.7rem',
                                      ml: 0.5,
                                      flexShrink: 0,
                                      '& .MuiChip-icon': {
                                        color: 'white',
                                      },
                                    }}
                                  />
                                )}
                              </Box>
                              {conversation.unreadCount > 0 && (
                                <Chip
                                  label={conversation.unreadCount}
                                  size="small"
                                  sx={{
                                    bgcolor: '#43A047',
                                    color: 'white',
                                    fontWeight: 700,
                                    height: 24,
                                    minWidth: 24,
                                    ml: 1,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  color: '#607D8B',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                }}
                              >
                                {conversation.lastMessage || 'Henüz mesaj yok'}
                              </Typography>
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  color: '#A5D6A7',
                                  fontWeight: 500,
                                  display: 'block',
                                }}
                              >
                                {formatDate(conversation.lastMessageTime)}
                              </Typography>
                            </>
                          }
                          secondaryTypographyProps={{
                            component: 'div',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Paper>

        {/* Chat Area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            minWidth: 0,
            maxWidth: '100vw',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage:
                'url(https://images.unsplash.com/photo-1490645935967-10de6ba17061?q=80&w=2000&auto=format&fit=crop)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.03,
              zIndex: 0,
              pointerEvents: 'none',
            },
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F9FAF7 100%)',
          }}
        >
          {selectedConversation ? (
            <>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderBottom: '2px solid #e2e8f0',
                  background:
                    'linear-gradient(135deg, #FFFFFF 0%, #F9FAF7 100%)',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {isMobile && (
                      <IconButton
                        onClick={() => setSelectedConversation(null)}
                        sx={{
                          width: 20,
                          color: '#263238',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <ArrowBackIcon />
                      </IconButton>
                    )}
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: '#43A047',
                        fontWeight: 700,
                      }}
                    >
                      {isDietitian
                        ? selectedConversation.clientName
                            .charAt(0)
                            .toUpperCase()
                        : selectedConversation.dietitianName
                            .charAt(0)
                            .toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#263238',
                        }}
                      >
                        {isDietitian
                          ? selectedConversation.clientName
                          : selectedConversation.dietitianName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#607D8B',
                        }}
                      >
                        Aktif
                      </Typography>
                    </Box>
                  </Box>

                  {/* Video Call Button */}
                  <IconButton
                    onClick={startVideoCall}
                    sx={{
                      bgcolor: '#43A047',
                      color: 'white',
                      width: 44,
                      height: 44,
                      '&:hover': {
                        bgcolor: '#388E3C',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <VideocamIcon />
                  </IconButton>
                </Box>
              </Paper>

              {/* Messages */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  p: 3,
                  position: 'relative',
                  zIndex: 1,
                  WebkitOverflowScrolling: 'touch',
                  width: '100%',
                  maxWidth: '100vw',
                  backgroundImage: `
                  radial-gradient(circle at 10% 20%, rgba(165, 214, 167, 0.03) 0%, transparent 50%),
                  radial-gradient(circle at 90% 80%, rgba(165, 214, 167, 0.03) 0%, transparent 50%)
                `,
                }}
              >
                {messages.map((message, index) => {
                  const isOwnMessage = message.senderId === currentUserId;
                  const showDate =
                    index === 0 ||
                    formatDate(messages[index - 1].timestamp) !==
                      formatDate(message.timestamp);

                  return (
                    <Box key={message.id}>
                      {showDate && (
                        <Box sx={{ textAlign: 'center', my: 3 }}>
                          <Chip
                            label={formatDate(message.timestamp)}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(165, 214, 167, 0.2)',
                              color: '#2E7D32',
                              fontWeight: 600,
                              border: '1px solid rgba(165, 214, 167, 0.4)',
                            }}
                          />
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: isOwnMessage
                            ? 'flex-end'
                            : 'flex-start',
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '70%',
                            bgcolor: isOwnMessage
                              ? 'linear-gradient(135deg, #43A047 0%, #66BB6A 100%)'
                              : '#FFFFFF',
                            color: isOwnMessage ? 'white' : '#263238',
                            borderRadius: isOwnMessage
                              ? '20px 20px 4px 20px'
                              : '20px 20px 20px 4px',
                            px: 2,
                            py: 1.5,
                            boxShadow: isOwnMessage
                              ? '0 2px 8px rgba(67, 160, 71, 0.25)'
                              : '0 2px 8px rgba(0, 0, 0, 0.08)',
                            background: isOwnMessage
                              ? 'linear-gradient(135deg, #43A047 0%, #66BB6A 100%)'
                              : '#FFFFFF',
                            border: isOwnMessage ? 'none' : '1px solid #e2e8f0',
                          }}
                        >
                          {message.attachmentUrl && (
                            <Box sx={{ mb: message.content ? 1 : 0 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.75,
                                  alignItems: 'center',
                                }}
                              >
                                <Button
                                  size="small"
                                  variant={isOwnMessage ? 'contained' : 'outlined'}
                                  startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                                  onClick={() =>
                                    window.open(message.attachmentUrl!, '_blank')
                                  }
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    px: 1,
                                    ...(isOwnMessage
                                      ? {
                                          bgcolor: 'rgba(255,255,255,0.25)',
                                          color: 'white',
                                          '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                                        }
                                      : {
                                          borderColor: colors.primary.main,
                                          color: colors.primary.main,
                                          '&:hover': {
                                            borderColor: colors.primary.dark,
                                            bgcolor: 'rgba(67, 160, 71, 0.08)',
                                          },
                                        }),
                                  }}
                                >
                                  Görüntüle
                                </Button>
                                <Button
                                  size="small"
                                  variant={isOwnMessage ? 'contained' : 'outlined'}
                                  startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = message.attachmentUrl!;
                                    link.download =
                                      message.attachmentFileName ||
                                      'dosya';
                                    link.click();
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    px: 1,
                                    ...(isOwnMessage
                                      ? {
                                          bgcolor: 'rgba(255,255,255,0.25)',
                                          color: 'white',
                                          '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                                        }
                                      : {
                                          borderColor: colors.primary.main,
                                          color: colors.primary.main,
                                          '&:hover': {
                                            borderColor: colors.primary.dark,
                                            bgcolor: 'rgba(67, 160, 71, 0.08)',
                                          },
                                        }),
                                  }}
                                >
                                  İndir
                                </Button>
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  opacity: isOwnMessage ? 0.9 : 0.7,
                                  mt: 0.25,
                                  fontSize: '0.7rem',
                                }}
                              >
                                {message.attachmentFileName || 'Dosya'}
                              </Typography>
                            </Box>
                          )}
                          {message.content ? (
                            <Typography
                              variant="body2"
                              sx={{
                                lineHeight: 1.6,
                                wordBreak: 'break-word',
                              }}
                            >
                              {message.content}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              opacity: isOwnMessage ? 0.9 : 0.6,
                              mt: 0.5,
                              fontSize: '0.7rem',
                              textAlign: 'right',
                            }}
                          >
                            {formatTime(message.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>

              {/* Message Input */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderTop: '2px solid #e2e8f0',
                  background: '#FFFFFF',
                  position: 'relative',
                  zIndex: 1,
                  width: '100%',
                  maxWidth: '100vw',
                  boxSizing: 'border-box',
                }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,image/*,.txt"
                  style={{ display: 'none' }}
                />
                {selectedFile && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                      p: 1,
                      borderRadius: 2,width: 'fit-content',
                      bgcolor: '#E8F5E9',
                      border: '1px solid rgba(67, 160, 71, 0.3)',
                    }}
                  >
                    <AttachFileIcon sx={{ color: '#43A047', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      {selectedFile.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setSelectedFile(null)}
                      sx={{
                        color: '#d32f2f',
                        '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' },
                        width: 'fit-content',
                      }}
                      title="Dosyayı kaldır"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-end',
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!selectedConversation || uploadingFile}
                    sx={{
                      color: '#43A047',
                      flexShrink: 0,
                      width: 'fit-content',
                      '&:hover': { bgcolor: 'rgba(67, 160, 71, 0.1)' },
                      '&:disabled': { color: '#bdbdbd' },
                    }}
                    title="Dosya ekle"
                  >
                    {uploadingFile ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <AttachFileIcon />
                    )}
                  </IconButton>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Mesajınızı yazın veya dosya ekleyin..."
                    variant="outlined"
                    size="small"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        background: '#F9FAF7',
                        '&:hover fieldset': {
                          borderColor: '#43A047',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#43A047',
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendClick}
                    disabled={(!newMessage.trim() && !selectedFile) || uploadingFile}
                    endIcon={<SendIcon />}
                    sx={{
                      bgcolor: '#43A047',
                      px: { xs: 2, sm: 3 },
                      py: 1.5,
                      borderRadius: 3,
                      width: 40,
                      minWidth: { xs: 80, sm: 120 },
                      flexShrink: 0,
                      '&:hover': {
                        bgcolor: '#388E3C',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(67, 160, 71, 0.3)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {isMobile ? '' : 'Gönder'}
                  </Button>
                </Box>
              </Paper>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                gap: 3,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background:
                    'linear-gradient(135deg, #43A047 0%, #66BB6A 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(67, 160, 71, 0.3)',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      transform: 'scale(1)',
                      boxShadow: '0 8px 24px rgba(67, 160, 71, 0.3)',
                    },
                    '50%': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 12px 32px rgba(67, 160, 71, 0.4)',
                    },
                  },
                }}
              >
                <SendIcon sx={{ fontSize: 60, color: 'white' }} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ color: '#263238', mb: 1 }}
                >
                  Mesajlaşmaya Başlayın
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Bir konuşma seçin veya yeni bir sohbet başlatın
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Messaging;
